import React from "react";
import {inject, observer} from "mobx-react";
import {Button, Card, CardHeader, Container, Input} from "reactstrap";
import classnames from "classnames";
import Api from "../../../../util/api";
import configuration from "../../../../configuration/configuration";
import CreateProduct from "../CreateProduct";
import CreateMarque from "../CreateMarque";
import {
    compterFiltres,
    compterParMarque,
    FILTRES,
    filtrerMarques,
    filtrerProduits,
    idsDoublons,
    initiales,
    nombrePrestations
} from "../../../../util/catalogue";
import "./Catalogue.scss";

function imageDe(element, dossier) {
    if (element?.asset?.filename) {
        return configuration.PhotoBase + element.asset.filename
    }
    if (element?.photo) {
        return configuration.Photo + dossier + "/" + element.photo
    }
    return null
}

function Vignette({element, dossier, nom, className}) {
    const [etat, setEtat] = React.useState("chargement")
    const src = imageDe(element, dossier)
    // Les initiales restent visibles tant que l'image n'est pas arrivée (ou si elle manque).
    return (
        <span className={classnames("catalogue-vignette", className)} title={nom}>
            {initiales(nom)}
            {src && etat !== "erreur" && (
                <img src={src} alt="" className={etat === "ok" ? "catalogue-vignette-ok" : ""}
                     onLoad={() => setEtat("ok")} onError={() => setEtat("erreur")}/>
            )}
        </span>
    )
}

const Catalogue = inject("rootStore")(observer(class Catalogue extends React.Component {

    get injected() {
        return this.props;
    }

    constructor(props, context) {
        super(props, context);
        this.api = new Api();
        const params = new URLSearchParams(this.props.location?.search ?? "")
        this.state = {
            marques: [],
            produits: [],
            chargement: true,
            recherche: "",
            filtre: params.get("filtre") ?? "tous",
            marqueId: params.get("marque") ? parseInt(params.get("marque"), 10) : null,
            produitId: params.get("produit") ? parseInt(params.get("produit"), 10) : null,
            fiche: params.get("produit") ? "produit" : (params.get("marque") ? "marque" : null),
            compact: false,
        }
        this.injected.rootStore.savedDatasStore.setHeader("Catalogue", "Marques et produits", "annuaire_marque.jpg")
    }

    async componentDidMount() {
        await this.charger()
    }

    async charger() {
        try {
            const [marques, produits] = await Promise.all([
                this.api.marques.getMarques(""),
                this.api.products.getProducts("", ""),
            ])
            this.setState({marques: marques?.datas ?? [], produits: produits?.datas ?? [], chargement: false})
        } catch (e) {
            this.setState({chargement: false})
        }
    }

    majUrl() {
        const params = new URLSearchParams()
        if (this.state.marqueId) params.set("marque", this.state.marqueId)
        if (this.state.fiche === "produit" && this.state.produitId) params.set("produit", this.state.produitId)
        if (this.state.filtre !== "tous") params.set("filtre", this.state.filtre)
        const search = params.toString()
        if (this.injected.history) this.injected.history.replace({pathname: this.injected.location.pathname, search: search ? "?" + search : ""})
    }

    choisir(changements) {
        this.setState(changements, () => this.majUrl())
    }

    choisirMarque(marque) {
        this.choisir({marqueId: marque.id, produitId: null, fiche: "marque", compact: false})
    }

    choisirProduit(produit) {
        this.injected.rootStore.annuaireStore.prestationStore.prestations = []
        // Sur un écran étroit, la fiche prend directement la place.
        this.choisir({produitId: produit.id, fiche: "produit", compact: window.innerWidth < 1400})
    }

    choisirFiltre(cle) {
        this.choisir({filtre: this.state.filtre === cle ? "tous" : cle, compact: false})
    }

    nouveauProduit() {
        this.choisir({produitId: null, fiche: "nouveauProduit", compact: false})
    }

    nouvelleMarque() {
        this.choisir({marqueId: null, produitId: null, fiche: "nouvelleMarque", compact: false})
    }

    fermerFiche() {
        this.choisir({produitId: null, fiche: null, compact: false})
    }

    async apresEnregistrementProduit(produit) {
        await this.charger()
        if (produit?.id && this.state.fiche === "nouveauProduit") {
            this.choisir({produitId: produit.id, marqueId: produit.annuaire_marque_id ?? this.state.marqueId, fiche: "produit"})
        }
    }

    async apresEnregistrementMarque(marque) {
        await this.charger()
        if (marque?.id && this.state.fiche === "nouvelleMarque") {
            this.choisir({marqueId: marque.id, fiche: "marque"})
        }
    }

    async apresSuppressionProduit() {
        this.fermerFiche()
        await this.charger()
    }

    async apresSuppressionMarque() {
        this.choisir({marqueId: null, produitId: null, fiche: null, compact: false})
        await this.charger()
    }

    // La fiche s'élargit dès qu'on travaille dedans ; un clic dans les listes les rouvre.
    activerFiche = () => {
        if (!this.state.compact && this.state.fiche) {
            this.setState({compact: true})
        }
    }

    activerListes = () => {
        if (this.state.compact) {
            this.setState({compact: false})
        }
    }

    renderBarre(produitsPortee) {
        const compte = compterFiltres(produitsPortee)
        return (
            <div className="catalogue-barre">
                <Input
                    className="catalogue-recherche"
                    type="search"
                    placeholder="Rechercher une marque ou un produit…"
                    value={this.state.recherche}
                    onChange={e => this.setState({recherche: e.target.value, compact: false})}
                />
                <div className="catalogue-filtres" role="group" aria-label="Filtres">
                    {FILTRES.filter(f => f.cle !== "tous").map(f => (
                        <Button key={f.cle}
                                size="sm"
                                color={this.state.filtre !== f.cle ? "light" : (f.cle === "publies" ? "success" : (f.cle === "doublons" ? "danger" : "warning"))}
                                onClick={() => this.choisirFiltre(f.cle)}>
                            {f.titre} <span className="catalogue-compte">{compte[f.cle]}</span>
                        </Button>
                    ))}
                </div>
                <div className="catalogue-ajouts">
                    <Button color="primary" size="sm" onClick={() => this.nouvelleMarque()}>
                        <i className="fa fa-plus mr-1"/> Marque
                    </Button>
                    <Button color="primary" size="sm" onClick={() => this.nouveauProduit()}>
                        <i className="fa fa-plus mr-1"/> Produit
                    </Button>
                </div>
            </div>
        )
    }

    renderMarques(marques, compteParMarque) {
        return (
            <Card className="catalogue-colonne catalogue-marques">
                <CardHeader className="catalogue-colonne-entete">
                    <span className="catalogue-titre-long">Marques</span>
                    <span className="catalogue-compte">{marques.length}</span>
                </CardHeader>
                <div className="catalogue-liste">
                    {marques.map(marque => (
                        <button type="button"
                                key={marque.id}
                                title={marque.nom}
                                className={classnames("catalogue-ligne", {
                                    "catalogue-ligne--active": marque.id === this.state.marqueId,
                                    "catalogue-ligne--masquee": !marque.isVisible,
                                })}
                                onClick={() => this.choisirMarque(marque)}>
                            <Vignette element={marque} dossier="marque" nom={marque.nom}/>
                            <span className="catalogue-nom">{marque.nom}</span>
                            {!marque.isVisible && <i className="fa fa-eye-slash catalogue-oeil" title="Marque masquée sur le site"/>}
                            <span className="catalogue-compte">{compteParMarque[marque.id] ?? 0}</span>
                        </button>
                    ))}
                    {marques.length === 0 && !this.state.chargement && (
                        <p className="catalogue-vide">Aucune marque ne correspond.</p>
                    )}
                </div>
            </Card>
        )
    }

    renderProduits(produits, marque) {
        const doublons = idsDoublons(this.state.produits)
        const filtre = FILTRES.find(f => f.cle === this.state.filtre)
        let titre = marque ? marque.nom : "Toutes les marques"
        if (this.state.filtre !== "tous") titre += " · " + filtre.titre.toLowerCase()

        return (
            <Card className="catalogue-colonne catalogue-produits">
                <CardHeader className="catalogue-colonne-entete">
                    <span className="catalogue-nom" title={titre}>{titre}</span>
                    {marque && (
                        <Button size="sm" color="secondary" outline className="catalogue-titre-long"
                                onClick={() => this.choisir({produitId: null, fiche: "marque", compact: false})}>
                            Modifier
                        </Button>
                    )}
                    <span className="catalogue-compte">{produits.length}</span>
                </CardHeader>
                <div className="catalogue-liste">
                    {produits.map(produit => (
                        <button type="button"
                                key={produit.id}
                                title={produit.nom}
                                className={classnames("catalogue-ligne", {
                                    "catalogue-ligne--active": produit.id === this.state.produitId && this.state.fiche === "produit",
                                })}
                                onClick={() => this.choisirProduit(produit)}>
                            <Vignette element={produit} dossier="product" nom={produit.nom} className="catalogue-vignette--produit"/>
                            <span className="catalogue-nom">
                                {produit.nom}
                                {!marque && <small className="catalogue-sous-titre">{produit.marque?.nom}</small>}
                            </span>
                            <span className="catalogue-pastilles">
                                {doublons.has(produit.id) && <span className="badge badge-danger">Doublon</span>}
                                {produit.isVisible ?
                                    <span className="badge badge-success">Publié</span> :
                                    <span className="badge badge-warning">Masqué</span>}
                            </span>
                            <span className="catalogue-compte" title="Prestations publiées">{nombrePrestations(produit)}</span>
                        </button>
                    ))}
                    {produits.length === 0 && !this.state.chargement && (
                        <p className="catalogue-vide">
                            {!marque && this.state.filtre === "tous" && !this.state.recherche ?
                                "Choisissez une marque, ou un filtre au-dessus pour voir toutes les marques." :
                                "Aucun produit ne correspond."}
                        </p>
                    )}
                    {marque && (
                        <button type="button" className="catalogue-ligne catalogue-ajout" onClick={() => this.nouveauProduit()}>
                            <i className="fa fa-plus"/> <span className="catalogue-nom">Ajouter un produit {marque.nom}</span>
                        </button>
                    )}
                </div>
            </Card>
        )
    }

    renderEnteteFiche() {
        return (
            <span className="catalogue-fiche-boutons">
                <Button size="sm" color="secondary" outline
                        onMouseDown={e => e.stopPropagation()}
                        title={this.state.compact ? "Agrandir les listes" : "Agrandir la fiche"}
                        onClick={e => {
                            e.stopPropagation()
                            this.setState({compact: !this.state.compact})
                        }}>
                    <i className={this.state.compact ? "fa fa-compress" : "fa fa-expand"}/>
                </Button>
                <Button size="sm" color="secondary" outline title="Fermer" onClick={() => this.fermerFiche()}>
                    <i className="fa fa-times"/>
                </Button>
            </span>
        )
    }

    renderFiche() {
        const entete = this.renderEnteteFiche()
        switch (this.state.fiche) {
            case "produit":
                return <CreateProduct embedded key={"p" + this.state.produitId} id={this.state.produitId}
                                      entete={entete}
                                      onSaved={p => this.apresEnregistrementProduit(p)}
                                      onDeleted={() => this.apresSuppressionProduit()}/>
            case "nouveauProduit":
                return <CreateProduct embedded key={"np" + (this.state.marqueId ?? "")} marqueId={this.state.marqueId ?? ""}
                                      entete={entete}
                                      onSaved={p => this.apresEnregistrementProduit(p)}/>
            case "marque":
                return this.state.marqueId ? (
                    <CreateMarque embedded key={"m" + this.state.marqueId} id={this.state.marqueId}
                                  entete={entete}
                                  onSaved={m => this.apresEnregistrementMarque(m)}
                                  onDeleted={() => this.apresSuppressionMarque()}/>
                ) : null
            case "nouvelleMarque":
                return <CreateMarque embedded key="nm" entete={entete}
                                     onSaved={m => this.apresEnregistrementMarque(m)}/>
            default:
                return (
                    <Card className="catalogue-fiche-vide">
                        <p>Choisissez une marque puis un produit pour voir sa fiche, ses prestations et ses prises en charge.</p>
                    </Card>
                )
        }
    }

    render() {
        const marque = this.state.marques.find(m => m.id === this.state.marqueId)
        const produitsPortee = marque ? this.state.produits.filter(p => p.annuaire_marque_id === marque.id) : this.state.produits
        const marques = filtrerMarques(this.state.marques, this.state.produits, this.state.recherche)
        const produits = filtrerProduits(this.state.produits, {
            marqueId: this.state.marqueId,
            filtre: this.state.filtre,
            recherche: this.state.recherche,
        })

        return (
            <Container className="mt-2 catalogue" fluid>
                {this.renderBarre(produitsPortee)}
                <div className={classnames("catalogue-colonnes", {"catalogue-colonnes--compact": this.state.compact})}>
                    <div className="catalogue-gauche" onMouseDown={this.activerListes} onFocus={this.activerListes}>
                        {this.renderMarques(marques, compterParMarque(this.state.produits))}
                        {this.renderProduits(produits, marque)}
                    </div>
                    <div className="catalogue-fiche" onMouseDown={this.activerFiche} onFocus={this.activerFiche}>
                        {this.renderFiche()}
                    </div>
                </div>
            </Container>
        );
    }
}));

export default Catalogue;
